import React, { useState } from "react";
import { FaFilePdf, FaFileAlt, FaEye, FaTrash } from "react-icons/fa";
import toast from "react-hot-toast";

const FileThumbnailForHB = ({ files, onDelete,showDelete = true }) => {
  const [deletingFile, setDeletingFile] = useState(null);
  const [hoveredFile, setHoveredFile] = useState(null);

  if (!files || files.length === 0) {
    return (
      <p style={{ textAlign: "center", color: "#999", padding: "20px" }}>
        No files uploaded
      </p>
    );
  }

  const getFileType = (url) => {
    if (!url) return "other";
    const ext = url.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif"].includes(ext)) return "image";
    if (ext === "pdf") return "pdf";
    return "other";
  };

  const handleDeleteFile = async (file) => {
    if (!file.newFilename) {
      toast.error("Error: Could not identify file");
      return;
    }

    const fileName = file.originalFilename || file.newFilename;
    if (!window.confirm(`Delete "${fileName}"?`)) {
      return;
    }

    setDeletingFile(file.newFilename);

    try {
      await onDelete(file);
      setDeletingFile(null);
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete file");
      setDeletingFile(null);
    }
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: "16px",
        width: "100%",
        padding: "16px 0",
      }}
    >
      {files &&
        files.length > 0 &&
        files.map((file, index) => {
          if (!file || !file.newFilename) return null;

          // Use _uniqueId if available, otherwise fall back to newFilename
          const uniqueKey =
            file._uniqueId || file._id || `${file.newFilename}-${index}`;
          const fileUrl = file.Location || file.fileUrl || file.url || file.key;
          const type = getFileType(fileUrl);
          const isDeleting = deletingFile === file.newFilename;
          const isHovered = hoveredFile === uniqueKey;

          return (
            <div
              key={uniqueKey}
              onMouseEnter={() => setHoveredFile(uniqueKey)}
              onMouseLeave={() => setHoveredFile(null)}
              style={{
                border: "1px solid #ddd",
                borderRadius: "8px",
                overflow: "hidden",
                background: "#f9f9f9",
                transition: "all 0.2s ease",
                boxShadow: isHovered ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
                cursor: "pointer",
                position: "relative",
              }}
            >
              {/* File Preview */}
              <div
                style={{ width: "100%", height: "120px", position: "relative" }}
              >
                {type === "image" ? (
                  <img
                    src={fileUrl}
                    alt={file.originalFilename || "preview"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : type === "pdf" ? (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#ffe6e6",
                    }}
                  >
                    <FaFilePdf size={48} color="#dc3545" />
                  </div>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    <FaFileAlt size={48} color="#6c757d" />
                  </div>
                )}

                {/* File Info - Show on hover */}
                {isHovered && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "12px",
                      animation: "fadeIn 0.2s ease",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "12px",
                        textAlign: "center",
                        wordBreak: "break-word",
                        margin: 0,
                        lineHeight: "1.4",
                      }}
                    >
                      {file.originalFilename || file.newFilename}
                    </p>
                  </div>
                )}
              </div>

              {/* File Name Below Preview */}
              <div
                style={{
                  padding: "8px",
                  fontSize: "11px",
                  textAlign: "center",
                  color: "#666",
                  borderTop: "1px solid #eee",
                  minHeight: "40px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  wordBreak: "break-word",
                }}
              >
                <div style={{ fontWeight: "500", color: "#333" }}>
                  {file.originalFilename
                    ? file.originalFilename.substring(0, 20)
                    : file.newFilename.substring(0, 20)}
                  {(file.originalFilename || file.newFilename).length > 20
                    ? "..."
                    : ""}
                </div>
                {file.size && (
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#999",
                      marginTop: "2px",
                    }}
                  >
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                )}
              </div>

              {/* Action Buttons - Always visible */}
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  padding: "8px",
                  background: "#f0f0f0",
                  borderTop: "1px solid #ddd",
                }}
              >
                {/* View Button */}
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                    padding: "6px 4px",
                    border: "none",
                    borderRadius: "4px",
                    fontSize: "11px",
                    fontWeight: 500,
                    cursor: "pointer",
                    background: "#007bff",
                    color: "white",
                    textDecoration: "none",
                    opacity: isDeleting ? 0.5 : 1,
                    pointerEvents: isDeleting ? "none" : "auto",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    !isDeleting && (e.target.style.background = "#0056b3")
                  }
                  onMouseLeave={(e) =>
                    !isDeleting && (e.target.style.background = "#007bff")
                  }
                  title="View file"
                >
                  <FaEye size={11} /> View
                </a>
                                {/* Delete Button */}

{showDelete &&
                <button
                  onClick={() => handleDeleteFile(file)}
                  disabled={isDeleting}
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                    padding: "6px 4px",
                    border: "none",
                    borderRadius: "4px",
                    fontSize: "11px",
                    fontWeight: 500,
                    cursor: isDeleting ? "not-allowed" : "pointer",
                    background: "#dc3545",
                    color: "white",
                    opacity: isDeleting ? 0.5 : 1,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    !isDeleting && (e.target.style.background = "#c82333")
                  }
                  onMouseLeave={(e) =>
                    !isDeleting && (e.target.style.background = "#dc3545")
                  }
                  title="Delete file"
                >
                  <FaTrash size={11} /> {isDeleting ? "Deleting..." : "Delete"}
                </button>
        }
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default FileThumbnailForHB;
